
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Calendar, Clock, MapPin, Users, DollarSign, Globe, Link, Lock } from 'lucide-react'
import { Body, Button, ButtonVariant, ButtonStyle, Input, Select, DateInput } from '@meiuca/ui-components'
import type { SelectOption } from '@meiuca/ui-components'
import { 
  CreateGameData, 
  GameAccessType, 
  GameModality, 
  GameLevel, 
  ChargeType,
  getAllGameModalities,
  getAllGameLevels,
  getGameModalityLabel,
  getGameLevelLabel 
} from '@/types/game'
import { PlaceAutocomplete } from './PlaceAutocomplete'
import { formatDateTimeForInput, normalizeTimeStep } from '@meiuca/web-foundation'

interface CreateGameModalProps {
  isOpen: boolean
  onClose: () => void
  onCreateGame: (gameData: CreateGameData) => Promise<any>
  onSuccess?: (message: string) => void // Callback para notificação de sucesso
  onError?: (message: string) => void // Callback para notificação de erro
}

export function CreateGameModal({ isOpen, onClose, onCreateGame, onSuccess, onError }: CreateGameModalProps) {
  // Opções para os selects do design system
  const modalityOptions: SelectOption[] = getAllGameModalities().map(modality => ({
    value: modality.value,
    label: modality.label
  }))

  const levelOptions: SelectOption[] = getAllGameLevels().map(level => ({
    value: level.value,
    label: level.label
  }))

  const [formData, setFormData] = useState<CreateGameData>({
    startTime: '',
    endTime: '',
    accessType: GameAccessType.PUBLIC,
    modality: GameModality.FOOTBALL,
    name: '',
    level: GameLevel.OPEN,
    minParticipants: 2,
    maxParticipants: 10, // Valor mais realista
    charge: {
      type: ChargeType.DIVIDED,
      amount: 0
    },
    location: {
      address: '',
      name: '',
      googlePlaceID: null,
      coordinate: {
        latitude: 0,
        longitude: 0
      }
    }
  })

  const [currentStep, setCurrentStep] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const totalSteps = 4

  const handleInputChange = (field: string, value: any) => {
    // Limpa erro do campo quando usuário começar a digitar
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }

    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setFormData(prev => {
        const parentData = prev[parent as keyof CreateGameData]
        return {
          ...prev,
          [parent]: {
            ...(typeof parentData === 'object' && parentData !== null ? parentData : {}),
            [child]: value
          }
        }
      })
    } else {
      setFormData(prev => {
        const coercedValue = (field === 'startTime' || field === 'endTime') ? normalizeTimeStep(value) : value
        const next = { ...prev, [field]: coercedValue } as CreateGameData
        // Se o startTime mudar e o endTime for anterior, limpa o endTime
        if (field === 'startTime' && next.endTime) {
          const start = new Date(coercedValue)
          const end = new Date(next.endTime)
          if (end <= start) {
            next.endTime = ''
          }
        }
        return next
      })
    }
  }

  const validateStep1 = (): Record<string, string> => {
    const errors: Record<string, string> = {}

    // Step 1: Informações Básicas
    if (!formData.name.trim()) {
      errors.name = 'Nome do jogo é obrigatório'
    }

    return errors
  }

  const validateStep2 = (): Record<string, string> => {
    const errors: Record<string, string> = {}

    // Step 2: Data e Horário + Tipo de Acesso
    if (!formData.startTime) {
      errors.startTime = 'Data e hora de início são obrigatórias'
    }

    if (!formData.endTime) {
      errors.endTime = 'Data e hora de fim são obrigatórias'
    }

    if (formData.startTime && formData.endTime) {
      const startDate = new Date(formData.startTime)
      const endDate = new Date(formData.endTime)
      if (endDate <= startDate) {
        errors.endTime = 'Data de fim deve ser posterior à data de início'
      }
    }

    return errors
  }

  const validateStep3 = (): Record<string, string> => {
    const errors: Record<string, string> = {}

    // Step 3: Participantes e Cobrança
    if (formData.minParticipants < 2) {
      errors.minParticipants = 'Mínimo de participantes deve ser pelo menos 2'
    }

    if (formData.maxParticipants < formData.minParticipants) {
      errors.maxParticipants = 'Máximo de participantes deve ser maior que o mínimo'
    }

    if (formData.charge.amount < 0) {
      errors.amount = 'Valor não pode ser negativo'
    }

    return errors
  }

  const validateStep4 = (): Record<string, string> => {
    const errors: Record<string, string> = {}

    // Step 4: Local
    if (!formData.location.name.trim() || !formData.location.address.trim()) {
      errors.location = 'Local é obrigatório'
    }

    return errors
  }

  const validateCurrentStep = (): Record<string, string> => {
    switch (currentStep) {
      case 1: return validateStep1()
      case 2: return validateStep2()
      case 3: return validateStep3()
      case 4: return validateStep4()
      default: return {}
    }
  }

  const validateAllSteps = (): Record<string, string> => {
    return {
      ...validateStep1(),
      ...validateStep2(),
      ...validateStep3(),
      ...validateStep4()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validar todos os steps antes de enviar
    const validationErrors = validateAllSteps()
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors)
      const firstError = Object.values(validationErrors)[0]
      if (onError) {
        onError(`❌ ${firstError}`)
      } else {
        setError(firstError)
      }
      return
    }

    setIsLoading(true)

    try {
      await onCreateGame(formData)
      
      // Sucesso - mostrar toast e fechar modal
      if (onSuccess) {
        onSuccess(`🎯 Jogo "${formData.name}" criado com sucesso!`)
      }
      
      // Reset form e fechar modal
      resetForm()
      onClose()
    } catch (error) {
      // Erro - mostrar toast de erro e manter modal aberto
      const errorMessage = error instanceof Error ? error.message : 'Erro ao criar jogo'
      
      if (onError) {
        onError(`❌ ${errorMessage}`)
      } else {
        // Fallback para exibição inline se não houver callback
        setError(errorMessage)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      startTime: '',
      endTime: '',
      accessType: GameAccessType.PUBLIC,
      modality: GameModality.FOOTBALL,
      name: '',
      level: GameLevel.OPEN,
      minParticipants: 2,
      maxParticipants: 10,
      charge: {
        type: ChargeType.DIVIDED,
        amount: 0
      },
      location: {
        address: '',
        name: '',
        googlePlaceID: null,
        coordinate: {
          latitude: 0,
          longitude: 0
        }
      }
    })
    setCurrentStep(1)
    setError(null)
    setFieldErrors({})
    setIsLoading(false)
  }

  // Removido - agora usa o helper centralizado em dateFormatters.ts

  const getAccessTypeIcon = (type: GameAccessType) => {
    switch (type) {
      case GameAccessType.PUBLIC: return Globe
      case GameAccessType.LINK_ONLY: return Link
      case GameAccessType.PRIVATE: return Lock
    }
  }

  const getAccessTypeLabel = (type: GameAccessType) => {
    switch (type) {
      case GameAccessType.PUBLIC: return 'Público'
      case GameAccessType.LINK_ONLY: return 'Apenas por Link'
      case GameAccessType.PRIVATE: return 'Privado'
    }
  }

  const getChargeTypeLabel = (type: ChargeType) => {
    switch (type) {
      case ChargeType.DIVIDED: return 'Valor Dividido'
      case ChargeType.FIXED: return 'Valor Fixo'
    }
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-text-primary mb-2">Informações Básicas</h3>
              <Body className="text-text-secondary">Vamos começar com as informações principais do seu jogo</Body>
            </div>

            {/* Nome do Jogo */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Nome do Jogo *
              </label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Ex: Pelada da Terça"
                fullWidth
                error={!!fieldErrors.name}
                errorMessage={fieldErrors.name}
                required
              />
            </div>

            {/* Modalidade */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Modalidade *
              </label>
              <Select
                options={modalityOptions}
                value={formData.modality}
                onChange={(value) => handleInputChange('modality', value)}
                placeholder="Selecione uma modalidade"
                fullWidth
                required
              />
            </div>

            {/* Nível */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Nível *
              </label>
              <Select
                options={levelOptions}
                value={formData.level}
                onChange={(value) => handleInputChange('level', value)}
                placeholder="Selecione um nível"
                fullWidth
                required
              />
            </div>

          </div>
        )

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-text-primary mb-2">Data e Horário</h3>
              <Body className="text-text-secondary">Quando será realizado o jogo?</Body>
            </div>

            {/* Data e Hora de Início */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Data e Hora de Início *
              </label>
              <DateInput
                value={formData.startTime}
                onChange={(value) => handleInputChange('startTime', value)}
                error={!!fieldErrors.startTime}
                errorMessage={fieldErrors.startTime}
                fullWidth
                required
              />
            </div>

            {/* Data e Hora de Fim */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                <Clock className="w-4 h-4 inline mr-2" />
                Data e Hora de Término *
              </label>
              <DateInput
                value={formData.endTime}
                min={formData.startTime || undefined}
                onChange={(value) => handleInputChange('endTime', value)}
                error={!!fieldErrors.endTime}
                errorMessage={fieldErrors.endTime}
                fullWidth
                required
              />
            </div>

            {/* Tipo de Acesso */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Tipo de Acesso *
              </label>
              <div className="grid grid-cols-1 gap-3">
                {Object.values(GameAccessType).map(type => {
                  const IconComponent = getAccessTypeIcon(type)
                  return (
                    <label key={type} className="cursor-pointer">
                      <input
                        type="radio"
                        name="accessType"
                        value={type}
                        checked={formData.accessType === type}
                        onChange={(e) => handleInputChange('accessType', e.target.value)}
                        className="sr-only"
                      />
                      <div className={`flex items-center space-x-3 p-3 border rounded-lg transition-all ${
                        formData.accessType === type
                          ? 'border-brand-primary bg-brand-primary/10'
                          : 'border-border-primary hover:border-border-secondary'
                      }`}>
                        <IconComponent className="w-5 h-5 text-brand-secondary" />
                        <div>
                          <div className="font-medium text-text-primary">{getAccessTypeLabel(type)}</div>
                          <div className="text-sm text-text-secondary">
                            {type === GameAccessType.PUBLIC && 'Qualquer pessoa pode encontrar e participar'}
                            {type === GameAccessType.LINK_ONLY && 'Apenas pessoas com o link podem participar'}
                            {type === GameAccessType.PRIVATE && 'Apenas pessoas convidadas podem participar'}
                          </div>
                        </div>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>
          </div>
        )

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-text-primary mb-2">Participantes e Cobrança</h3>
              <Body className="text-text-secondary">Configure as vagas e valores do jogo</Body>
            </div>

            {/* Participantes */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Mínimo de Participantes *
                </label>
                <Input
                  type="number"
                  value={formData.minParticipants.toString()}
                  onChange={(e) => handleInputChange('minParticipants', parseInt(e.target.value) || 1)}
                  min="1"
                  max="50"
                  fullWidth
                  error={!!fieldErrors.minParticipants}
                  errorMessage={fieldErrors.minParticipants}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Máximo de Participantes *
                </label>
                <Input
                  type="number"
                  value={formData.maxParticipants.toString()}
                  onChange={(e) => handleInputChange('maxParticipants', parseInt(e.target.value) || 2)}
                  min="1"
                  max="50"
                  fullWidth
                  error={!!fieldErrors.maxParticipants}
                  errorMessage={fieldErrors.maxParticipants}
                  required
                />
              </div>
            </div>

            {/* Tipo de Cobrança */}
            <div>
              <div className="mb-4">
                <label className="block text-lg font-semibold text-text-primary mb-2">
                  💰 Como será a cobrança?
                </label>
                <p className="text-sm text-text-secondary">
                  Escolha como os participantes irão pagar pelo jogo
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Valor Dividido */}
                <label className="cursor-pointer">
                  <input
                    type="radio"
                    name="chargeType"
                    value={ChargeType.DIVIDED}
                    checked={formData.charge.type === ChargeType.DIVIDED}
                    onChange={(e) => handleInputChange('charge.type', e.target.value)}
                    className="sr-only"
                  />
                  <div className={`relative p-5 border-2 rounded-xl transition-all ${
                    formData.charge.type === ChargeType.DIVIDED
                      ? 'border-brand-primary bg-brand-primary/5 ring-2 ring-brand-primary/20'
                      : 'border-border-primary hover:border-brand-primary/50 hover:bg-brand-primary/5'
                  }`}>
                    <div className="flex items-start space-x-3">
                      <div className={`w-6 h-6 rounded-full border-2 mt-1 flex items-center justify-center ${
                        formData.charge.type === ChargeType.DIVIDED
                          ? 'border-brand-primary bg-brand-primary'
                          : 'border-border-secondary'
                      }`}>
                        {formData.charge.type === ChargeType.DIVIDED && (
                          <div className="w-3 h-3 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-2xl">🧮</span>
                          <span className="font-bold text-text-primary">Dividir Conta</span>
                        </div>
                        <p className="text-sm text-text-secondary mb-3">
                          Informe o <strong>custo total</strong> e será dividido igualmente entre os participantes
                        </p>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-xs text-blue-700">
                            <strong>Exemplo:</strong> Quadra custa R$ 120<br/>
                            Com 12 pessoas = R$ 10 por pessoa
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>

                {/* Valor Fixo */}
                <label className="cursor-pointer">
                  <input
                    type="radio"
                    name="chargeType"
                    value={ChargeType.FIXED}
                    checked={formData.charge.type === ChargeType.FIXED}
                    onChange={(e) => handleInputChange('charge.type', e.target.value)}
                    className="sr-only"
                  />
                  <div className={`relative p-5 border-2 rounded-xl transition-all ${
                    formData.charge.type === ChargeType.FIXED
                      ? 'border-brand-primary bg-brand-primary/5 ring-2 ring-brand-primary/20'
                      : 'border-border-primary hover:border-brand-primary/50 hover:bg-brand-primary/5'
                  }`}>
                    <div className="flex items-start space-x-3">
                      <div className={`w-6 h-6 rounded-full border-2 mt-1 flex items-center justify-center ${
                        formData.charge.type === ChargeType.FIXED
                          ? 'border-brand-primary bg-brand-primary'
                          : 'border-border-secondary'
                      }`}>
                        {formData.charge.type === ChargeType.FIXED && (
                          <div className="w-3 h-3 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-2xl">🎯</span>
                          <span className="font-bold text-text-primary">Valor Fixo</span>
                        </div>
                        <p className="text-sm text-text-secondary mb-3">
                          Cada participante paga um <strong>valor fixo</strong>, independente do número de pessoas
                        </p>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <p className="text-xs text-green-700">
                            <strong>Exemplo:</strong> R$ 25 por pessoa<br/>
                            Seja com 8 ou 16 pessoas
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Campo de Valor */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                {formData.charge.type === ChargeType.DIVIDED 
                  ? '💵 Custo Total da Quadra/Local *' 
                  : '💵 Valor por Participante *'
                }
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-secondary font-medium text-lg z-10">
                  R$
                </span>
                <Input
                  type="number"
                  value={formData.charge.amount.toString()}
                  onChange={(e) => handleInputChange('charge.amount', parseFloat(e.target.value) || 0)}
                  className="pl-12 pr-4 py-4 text-lg font-semibold"
                  fullWidth
                  min="0"
                  step="0.01"
                  placeholder={formData.charge.type === ChargeType.DIVIDED ? "120,00" : "25,00"}
                  error={!!fieldErrors.amount}
                  errorMessage={fieldErrors.amount}
                  required
                />
              </div>
              
              {/* Calculadora em tempo real */}
              {formData.charge.amount > 0 && formData.maxParticipants > 0 && (
                <div className="mt-4 p-4 bg-gradient-to-r from-brand-primary/5 to-brand-secondary/5 border border-brand-primary/20 rounded-xl">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-xl">📊</span>
                    <span className="font-semibold text-text-primary">Cálculo Automático</span>
                  </div>
                  
                  {formData.charge.type === ChargeType.DIVIDED ? (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-text-secondary">
                          Com {formData.maxParticipants} pessoas:
                        </span>
                        <span className="font-bold text-brand-primary text-lg">
                          R$ {(formData.charge.amount / formData.maxParticipants).toFixed(2)} por pessoa
                        </span>
                      </div>
                      {formData.minParticipants !== formData.maxParticipants && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-text-secondary">
                            Com {formData.minParticipants} pessoas:
                          </span>
                          <span className="font-bold text-orange-600">
                            R$ {(formData.charge.amount / formData.minParticipants).toFixed(2)} por pessoa
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-text-secondary">
                          Arrecadação com {formData.maxParticipants} pessoas:
                        </span>
                        <span className="font-bold text-brand-primary text-lg">
                          R$ {(formData.charge.amount * formData.maxParticipants).toFixed(2)} total
                        </span>
                      </div>
                      {formData.minParticipants !== formData.maxParticipants && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-text-secondary">
                            Arrecadação com {formData.minParticipants} pessoas:
                          </span>
                          <span className="font-bold text-orange-600">
                            R$ {(formData.charge.amount * formData.minParticipants).toFixed(2)} total
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-text-primary mb-2">Local</h3>
              <Body className="text-text-secondary">Onde será realizado o jogo?</Body>
            </div>

            {/* Seleção de Local com Google Places */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                <MapPin className="w-4 h-4 inline mr-2" />
                Local do Jogo *
              </label>
              <PlaceAutocomplete
                value={formData.location}
                onChange={(location) => handleInputChange('location', location)}
                placeholder="Busque por nome do local ou endereço..."
                required
              />
              <div className="mt-2 text-sm text-text-secondary">
                Digite o nome do estabelecimento ou endereço para buscar automaticamente
              </div>
            </div>

            {/* Endereço Selecionado */}
            {formData.location.address && formData.location.googlePlaceID && (
              <div className="p-3 bg-background-secondary rounded-lg border border-border-primary">
                <div className="flex items-start space-x-2">
                  <MapPin className="w-4 h-4 text-brand-secondary mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-text-primary">{formData.location.name}</div>
                    <div className="text-sm text-text-secondary">{formData.location.address}</div>
                    {formData.location.coordinate.latitude !== 0 && formData.location.coordinate.longitude !== 0 && (
                      <div className="text-xs text-text-secondary mt-1">
                        📍 {formData.location.coordinate.latitude.toFixed(6)}, {formData.location.coordinate.longitude.toFixed(6)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-text-primary mb-2">Participantes e Valor</h3>
              <Body className="text-text-secondary">Defina quantas pessoas podem participar e os custos</Body>
            </div>

            {/* Número de Participantes - Vagas */}
            <div>
              <div className="mb-3">
                <h4 className="text-lg font-semibold text-text-primary mb-1">Vagas do Jogo</h4>
                <Body className="text-text-secondary text-sm">Defina quantas pessoas podem participar do jogo</Body>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    <Users className="w-4 h-4 inline mr-2" />
                    Mínimo de Participantes *
                  </label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.minParticipants.toString()}
                    onChange={(e) => handleInputChange('minParticipants', parseInt(e.target.value) || 1)}
                    placeholder="Ex: 6"
                    required
                  />
                  <div className="text-xs text-text-secondary mt-1">
                    Mínimo para o jogo acontecer
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Máximo de Participantes *
                  </label>
                  <Input
                    type="number"
                    min={formData.minParticipants}
                    value={formData.maxParticipants.toString()}
                    onChange={(e) => handleInputChange('maxParticipants', parseInt(e.target.value) || 1)}
                    placeholder="Ex: 22"
                    required
                  />
                  <div className="text-xs text-text-secondary mt-1">
                    Limite máximo de vagas
                  </div>
                </div>
              </div>
              
              {/* Indicador de vagas */}
              <div className="mt-3 p-3 bg-background-secondary rounded-lg border border-border-primary">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary">Total de vagas:</span>
                  <span className="font-semibold text-brand-primary">
                    {formData.maxParticipants} vagas
                  </span>
                </div>
                {formData.minParticipants > 0 && formData.maxParticipants > 0 && (
                  <div className="text-xs text-text-secondary mt-1">
                    O jogo precisa de pelo menos {formData.minParticipants} jogadores para acontecer
                  </div>
                )}
              </div>
            </div>

            {/* Tipo de Cobrança */}
            <div>
              <div className="mb-3">
                <h4 className="text-lg font-semibold text-text-primary mb-1">Como será cobrado?</h4>
                <Body className="text-text-secondary text-sm">Escolha como os participantes irão pagar pelo jogo</Body>
              </div>
              
              <div className="space-y-3">
                {/* Dividido por igual */}
                <label className="cursor-pointer block">
                  <input
                    type="radio"
                    name="chargeType"
                    value={ChargeType.DIVIDED}
                    checked={formData.charge.type === ChargeType.DIVIDED}
                    onChange={(e) => handleInputChange('charge.type', e.target.value)}
                    className="sr-only"
                  />
                  <div className={`p-4 border rounded-lg transition-all ${
                    formData.charge.type === ChargeType.DIVIDED
                      ? 'border-brand-primary bg-brand-primary/10 ring-2 ring-brand-primary/20'
                      : 'border-border-primary hover:border-border-secondary hover:bg-background-secondary/50'
                  }`}>
                    <div className="flex items-start space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center ${
                        formData.charge.type === ChargeType.DIVIDED
                          ? 'border-brand-primary bg-brand-primary'
                          : 'border-border-secondary'
                      }`}>
                        {formData.charge.type === ChargeType.DIVIDED && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-text-primary mb-1">
                          💰 Dividido por igual
                        </div>
                        <div className="text-sm text-text-secondary">
                          Informe o <strong>valor total da quadra/local</strong> que será dividido entre todos os participantes
                        </div>
                        <div className="text-xs text-brand-secondary mt-1">
                          Exemplo: Quadra custa R$ 100, com 10 pessoas = R$ 10 por pessoa
                        </div>
                      </div>
                    </div>
                  </div>
                </label>

                {/* Valor fixo */}
                <label className="cursor-pointer block">
                  <input
                    type="radio"
                    name="chargeType"
                    value={ChargeType.FIXED}
                    checked={formData.charge.type === ChargeType.FIXED}
                    onChange={(e) => handleInputChange('charge.type', e.target.value)}
                    className="sr-only"
                  />
                  <div className={`p-4 border rounded-lg transition-all ${
                    formData.charge.type === ChargeType.FIXED
                      ? 'border-brand-primary bg-brand-primary/10 ring-2 ring-brand-primary/20'
                      : 'border-border-primary hover:border-border-secondary hover:bg-background-secondary/50'
                  }`}>
                    <div className="flex items-start space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center ${
                        formData.charge.type === ChargeType.FIXED
                          ? 'border-brand-primary bg-brand-primary'
                          : 'border-border-secondary'
                      }`}>
                        {formData.charge.type === ChargeType.FIXED && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-text-primary mb-1">
                          🎯 Valor fixo por pessoa
                        </div>
                        <div className="text-sm text-text-secondary">
                          Informe o <strong>valor que cada pessoa</strong> deve pagar para participar
                        </div>
                        <div className="text-xs text-brand-secondary mt-1">
                          Exemplo: R$ 15 por pessoa, independente do número de participantes
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Valor */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                <DollarSign className="w-4 h-4 inline mr-2" />
                {formData.charge.type === ChargeType.DIVIDED ? 'Valor Total da Quadra (R$) *' : 'Valor por Pessoa (R$) *'}
              </label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.charge.amount.toString()}
                onChange={(e) => handleInputChange('charge.amount', parseFloat(e.target.value) || 0)}
                placeholder={formData.charge.type === ChargeType.DIVIDED ? "Ex: 100.00" : "Ex: 15.00"}
                required
              />
              
              {/* Calculadora de valor */}
              {formData.charge.amount > 0 && formData.maxParticipants > 0 && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="text-sm">
                    {formData.charge.type === ChargeType.DIVIDED ? (
                      <>
                        <div className="font-semibold text-green-800 mb-1">💡 Cálculo por pessoa:</div>
                        <div className="text-green-700">
                          • Com {formData.maxParticipants} pessoas: <strong>R$ {(formData.charge.amount / formData.maxParticipants).toFixed(2)} por pessoa</strong>
                        </div>
                        {formData.minParticipants !== formData.maxParticipants && (
                          <div className="text-green-700">
                            • Com {formData.minParticipants} pessoas: <strong>R$ {(formData.charge.amount / formData.minParticipants).toFixed(2)} por pessoa</strong>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="font-semibold text-green-800 mb-1">💡 Arrecadação total:</div>
                        <div className="text-green-700">
                          • Com {formData.maxParticipants} pessoas: <strong>R$ {(formData.charge.amount * formData.maxParticipants).toFixed(2)} total</strong>
                        </div>
                        {formData.minParticipants !== formData.maxParticipants && (
                          <div className="text-green-700">
                            • Com {formData.minParticipants} pessoas: <strong>R$ {(formData.charge.amount * formData.minParticipants).toFixed(2)} total</strong>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )

      default:
        return null
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-background-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border-primary">
            <div>
              <h2 className="text-xl font-semibold text-text-primary">Criar Novo Jogo</h2>
              <div className="flex items-center space-x-2 mt-2">
                {Array.from({ length: totalSteps }, (_, i) => (
                  <div
                    key={i}
                    className={`w-8 h-2 rounded-full transition-colors ${
                      i + 1 <= currentStep ? 'bg-brand-primary' : 'bg-border-primary'
                    }`}
                  />
                ))}
                <span className="text-sm text-text-secondary ml-2">
                  {currentStep} de {totalSteps}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-background-secondary rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-text-secondary" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit}>
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {/* Error Message */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                        <X className="w-3 h-3 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="text-red-800 font-medium text-sm mb-1">
                        Erro ao criar jogo
                      </div>
                      <div className="text-red-700 text-sm">
                        {error}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setError(null)}
                      className="flex-shrink-0 text-red-400 hover:text-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
              
              {renderStep()}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-border-primary bg-background-secondary/30">
              <div>
                {currentStep > 1 && (
                  <Button
                    variant={ButtonVariant.PRIMARY}
                    buttonStyle="clear"
                    type="button"
                    onClick={() => {
                      setCurrentStep(prev => prev - 1)
                      setError(null) // Limpa erro ao navegar
                    }}
                    disabled={isLoading}
                  >
                    Voltar
                  </Button>
                )}
              </div>
              <div className="space-x-3">
                <Button
                  buttonStyle={ButtonStyle.BORDERED}
                  size="md"
                  type="button"
                  onClick={() => {
                    resetForm()
                    onClose()
                  }}
                  disabled={isLoading}
                >
                  Cancelar
                </Button>
                {currentStep < totalSteps ? (
                  <Button
                    variant={ButtonVariant.PRIMARY}
                    size="md"
                    type="button"
                    onClick={() => {
                      // Validar step atual antes de avançar
                      const stepErrors = validateCurrentStep()
                      if (Object.keys(stepErrors).length > 0) {
                        setFieldErrors(stepErrors)
                        const firstError = Object.values(stepErrors)[0]
                        if (onError) {
                          onError(`❌ ${firstError}`)
                        } else {
                          setError(firstError)
                        }
                        return
                      }
                      
                      setCurrentStep(prev => prev + 1)
                      setError(null) // Limpa erro ao avançar
                      setFieldErrors({}) // Limpa erros de campo
                    }}
                    disabled={isLoading}
                  >
                    Próximo
                  </Button>
                ) : (
                  <Button
                    variant={ButtonVariant.PRIMARY}
                    size="md"
                    type="submit"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Criando...' : 'Criar Jogo'}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
